import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import { MiniTitle } from '@delmonicos/shared-design-system-react/src/components/typography/mini-title/mini-title';
import {
  Text,
  TextFontSize,
  TextWeight,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import { ChargePointIsLocked } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/charge-point/charge-point-is-locked.enum';
import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@app/core/locale/i18n';
import { useChargePointContext } from '@app/feature/charge-point/charge-point.context';

import * as css from '../../charge-point-configuration.module.scss';

export const ConfigurationTabComponent = (): React.JSX.Element => {
  const { t } = useTranslation();
  const chargePoint = useChargePointContext();
  const generalInformation = chargePoint.channels[0];

  return (
    <Stack
      className={css.configurationTab}
      direction="column"
      gap={GapEnum.L}
      flex={1}>
      <Card className={css.statusCard} gap={GapEnum.M}>
        <Stack
          alignItems="center"
          justifyContent="space-between"
          gap={GapEnum.L}>
          <Stack flex={1}>
            <AccentTitle
              as={HeadingEnum.H2}
              icon="electric-terminal"
              text={t('delmonicos-shared-sdk.outputs-charge-point.is-locked')}
              variant={AccentVariantEnum.Secondary}
            />
          </Stack>
          <Tag
            flex={1}
            className={classNames(
              css.statusTag,
              `bg-surface-${chargePoint.isLocked ? 'warning' : 'success'}`,
            )}>
            <Stack
              alignItems="center"
              justifyContent="center"
              gap={GapEnum.XS}
              flex={1}>
              <Icon
                name={
                  chargePoint.isLocked
                    ? 'terminal-maintenance'
                    : 'electric-terminal'
                }
              />
              <Stack flex={1} justifyContent="center">
                <Text
                  fontWeight={TextWeight.SemiBold}
                  fontSize={TextFontSize.Large}>
                  {t(
                    `delmonicos-shared-sdk.outputs-charge-point-is-locked.${chargePoint.isLocked ? ChargePointIsLocked.TRUE : ChargePointIsLocked.FALSE}`,
                  )}
                </Text>
              </Stack>
            </Stack>
          </Tag>
        </Stack>
        <Stack
          direction="column"
          justifyContent="space-between"
          gap={GapEnum.M}>
          <Stack alignItems="center" justifyContent="space-between" flex={1}>
            <MiniTitle>
              {t(
                'delmonicos-shared-sdk.outputs-base-with-timestamp.created-at',
              )}
            </MiniTitle>
            <Text>
              {chargePoint ? formatDate(chargePoint.createdAt, i18n) : '-'}
            </Text>
          </Stack>
          <Stack alignItems="center" justifyContent="space-between" flex={1}>
            <MiniTitle>
              {t(
                'delmonicos-shared-sdk.outputs-base-with-timestamp.updated-at',
              )}
            </MiniTitle>
            <Text>
              {chargePoint ? formatDate(chargePoint.updatedAt, i18n) : '-'}
            </Text>
          </Stack>
          <Stack alignItems="center" justifyContent="space-between" flex={1}>
            <MiniTitle>
              {t(
                'delmonicos-shared-sdk.outputs-charge-point.last-heartbeat-time',
              )}
            </MiniTitle>
            <Text>
              {chargePoint.lastHeartbeatTime
                ? formatDate(chargePoint.lastHeartbeatTime, i18n)
                : '-'}
            </Text>
          </Stack>
        </Stack>
      </Card>
      <Card className={css.configurationCard} flex={1} gap={GapEnum.M}>
        <AccentTitle
          as={HeadingEnum.H2}
          icon="settings-01"
          text={t(
            '$.charge-point-configuration.configuration-tab.configuration-card.title',
          )}
          variant={AccentVariantEnum.Primary}
        />
        <Stack alignItems="center" justifyContent="space-between">
          <MiniTitle>
            {t('delmonicos-shared-sdk.outputs-charge-point.identity')}
          </MiniTitle>
          <Text fontSize={TextFontSize.Large}>
            {chargePoint.identity ?? '-'}
          </Text>
        </Stack>
        <Stack alignItems="center" justifyContent="space-between">
          <MiniTitle>
            {t('delmonicos-shared-sdk.outputs-channel.ocpi-evse-id')}
          </MiniTitle>
          <Text fontSize={TextFontSize.Large}>
            {generalInformation?.ocpiEvseId ?? '-'}
          </Text>
        </Stack>
        <Stack alignItems="center" justifyContent="space-between">
          <MiniTitle>
            {t('delmonicos-shared-sdk.outputs-charge-point.iccid')}
          </MiniTitle>
          <Text fontSize={TextFontSize.Large}>{chargePoint.iccid ?? '-'}</Text>
        </Stack>
        <Stack alignItems="center" justifyContent="space-between">
          <MiniTitle>
            {t('delmonicos-shared-sdk.outputs-charge-point.imsi')}
          </MiniTitle>
          <Text fontSize={TextFontSize.Large}>{chargePoint.imsi ?? '-'}</Text>
        </Stack>
        <Stack alignItems="center" justifyContent="space-between">
          <MiniTitle>
            {t('delmonicos-shared-sdk.outputs-charge-point.firmware-version')}
          </MiniTitle>
          <Text fontSize={TextFontSize.Large}>
            {chargePoint.firmwareVersion ?? '-'}
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
};
